import type React from 'react';
import { useEffect, useState } from 'react';

import { Player } from '@clubspark-react/ui/core';
import CloseIcon from '@mui/icons-material/Close';
import { Grid } from '@mui/material';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { competitionEngine } from 'tods-competition-factory';

import StandardButton from 'components/buttons/StandardButton';
import PenaltyPointsCard from 'components/dialogs/penaltyDialog/components/PenaltyPointsCard';
import PenaltyParticipantsDialog from 'components/dialogs/penaltyDialog/PenaltyParticipantsDialog';
import PenaltyPointsDialog from 'components/dialogs/penaltyDialog/PenaltyPointsDialog';
import { useStyles } from 'components/dialogs/penaltyDialog/styles';
import TMXDialog from 'components/dialogs/TMXDialog';
import { BodyLarge } from 'components/typography/Typography';
import { type MatchUpInterface } from 'typedefs/store/scheduleTypes';
import { type ParticipantInterface, PenaltyAction, type PenaltyInterface } from 'typedefs/store/tmxTypes';
import { isValidDate } from 'utils/dateTimeUtils';
import { participantName } from 'utils/participant';

import FormLabel from './components/FormLabel';

interface SuspensionPointsDialogProps {
  open?: boolean;
  loading?: boolean;
  participants?: ParticipantInterface[];
  matchUp?: MatchUpInterface;
  structureId?: string;
  drawId?: string;
  eventId?: string;
  onClose: () => void;
  onExited?: () => void;
  onSave: (penaltyData: any) => void;
}

const EmptyPoints: React.FC<React.PropsWithChildren<{ className?: string }>> = ({ children }) => {
  const { classes } = useStyles();

  return <BodyLarge className={classes.emptyPoints}>{children}</BodyLarge>;
};

const PenaltyDialog: React.FC<React.PropsWithChildren<SuspensionPointsDialogProps>> = ({
  open,
  loading,
  matchUp,
  structureId,
  drawId,
  eventId,
  participants,
  onClose,
  onExited,
  onSave,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const [showPointsDialog, setShowPointsDialog] = useState(false);
  const [penaltyPoints, setPenaltyPoints] = useState<PenaltyInterface[]>([]);
  const [editPenaltyPoint, setEditPenaltyPoint] = useState<Partial<PenaltyInterface> | null>(null);
  const [selectedParticipant, setSelectedParticipant] = useState<ParticipantInterface | null>(null);
  const visiblePenalties = penaltyPoints.filter((point) => point.penaltyAction !== PenaltyAction.DELETE);

  const scheduledDateString =
    matchUp?.schedule?.scheduledDate && matchUp?.schedule?.time
      ? `${matchUp?.schedule?.scheduledDate} ${matchUp?.schedule?.time}`
      : '';

  const scheduledAt = scheduledDateString ? dayjs(scheduledDateString).startOf('day').toDate() : null;

  const selectParticipant = (participant: ParticipantInterface | null) => {
    if (!participant) {
      setSelectedParticipant(null);
      setPenaltyPoints([]);
      return;
    }

    const tournamentParticipant = competitionEngine.findParticipant({
      participantId: participant.participantId,
    });

    setSelectedParticipant(tournamentParticipant?.participant || null);
    setPenaltyPoints(tournamentParticipant?.participant?.penalties || []);
  };
  const handleAddPointClick = () => {
    setEditPenaltyPoint({
      penaltyAction: PenaltyAction.ADD,
      issuedAt: isValidDate(scheduledAt) ? scheduledAt.toISOString() : '',
    });
    setShowPointsDialog(true);
  };

  const handleAddPointDialogClose = () => {
    setShowPointsDialog(false);
  };

  const handleAddPoint = (participantPenalty: PenaltyInterface) => {
    setPenaltyPoints((oldPoints) => {
      const existingPenaltyIndex = oldPoints.findIndex((p) => p.penaltyId === participantPenalty.penaltyId);

      if (existingPenaltyIndex > -1) {
        oldPoints.splice(existingPenaltyIndex, 1, participantPenalty);

        return oldPoints;
      }

      return [...oldPoints, participantPenalty];
    });

    setEditPenaltyPoint(null);

    setShowPointsDialog(false);
  };

  const handleEditPoint = (participantPoint: PenaltyInterface) => {
    setEditPenaltyPoint({ penaltyAction: participantPoint.penaltyAction || PenaltyAction.UPDATE, ...participantPoint });
    setShowPointsDialog(true);
  };

  const handleRemovePoint = (participantPoint: PenaltyInterface) => {
    setPenaltyPoints((oldPoints) => {
      const newPoints: PenaltyInterface[] = [];

      oldPoints.forEach((point) => {
        if (point.penaltyId !== participantPoint.penaltyId) {
          newPoints.push(point);
          return;
        }

        if (!point.penaltyAction) {
          newPoints.push({ ...point, penaltyAction: PenaltyAction.DELETE });
          return;
        }
      });

      return newPoints;
    });
  };

  const handleParticipantSelect = (participant: ParticipantInterface) => {
    selectParticipant(participant);
  };

  const handleSave = () => {
    const penaltyData = penaltyPoints
      .filter((point) => Boolean(point.penaltyAction))
      .map(({ penaltyAction, createdAt, penaltyId, ...point }) =>
        penaltyAction === PenaltyAction.UPDATE
          ? {
              method: penaltyAction,
              params: {
                penaltyId,
                participantId: selectedParticipant.participantId,
                modifications: {
                  ...point,
                },
              },
            }
          : {
              method: penaltyAction,
              params: {
                ...point,
                penaltyId,
                matchUpId: matchUp?.matchUpId,
                structureId,
                drawId,
                eventId,
                createdAt,
                participantIds: [selectedParticipant.participantId],
              },
            },
      );

    onClose();

    if (penaltyData.length) {
      onSave(penaltyData);
    }
  };

  useEffect(() => {
    if (!open && !loading) {
      selectParticipant(null);
      return;
    }

    if (!selectedParticipant && participants.length === 1) {
      selectParticipant(participants[0]);
    }
  }, [participants, selectedParticipant, open, loading]);

  if (participants.length > 1 && !selectedParticipant) {
    return (
      <PenaltyParticipantsDialog
        open={open}
        participants={participants}
        onClose={onClose}
        onParticipantSelect={handleParticipantSelect}
      />
    );
  }

  return (
    <>
      <TMXDialog
        fullScreen
        BackdropProps={{ className: classes.tmxDialogBackdrop }}
        className={classes.tmxDialog}
        open={open}
        PaperProps={{ elevation: 0, className: classes.tmxDialogPaper }}
        TransitionProps={{ onExited }}
        onClose={onClose}
      >
        <div className={classes.dialogHeader}>
          <Typography className={classes.dialogTitle} variant="h2">
            {t('suspensionPoints')}
          </Typography>
          <IconButton className={classes.dialogClose} edge="end" size="large" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </div>

        <DialogContent className={classes.dialogContent}>
          {Boolean(participants.length) && (
            <div className={classes.dialogPlayerWrapper}>
              <FormLabel>{t('ply')}:</FormLabel>
              <Player
                index={1}
                player={{ name: participantName(selectedParticipant) }}
                showAdditionalInfo={false}
                sideIndex={1}
              />
            </div>
          )}
          {!visiblePenalties.length && <EmptyPoints>{t('noPointsIssued')}</EmptyPoints>}
          {visiblePenalties.map((point, ind) => (
            <PenaltyPointsCard
              key={ind}
              participantPenalty={point}
              onEdit={handleEditPoint}
              onRemove={handleRemovePoint}
            />
          ))}
          <StandardButton className={classes.addPointButton} variant="secondary" onClick={handleAddPointClick}>
            {t('addPoint')}
          </StandardButton>
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <div className={classes.dialogActionsContent}>
            <Grid container spacing={2}>
              <Grid item>
                <StandardButton onClick={onClose}>{t('Close')}</StandardButton>
              </Grid>
              <Grid item>
                <StandardButton loading={loading} variant="primary" onClick={handleSave}>
                  {t('confirmSave')}
                </StandardButton>
              </Grid>
            </Grid>
          </div>
        </DialogActions>
      </TMXDialog>
      <PenaltyPointsDialog
        open={showPointsDialog}
        participant={selectedParticipant}
        participantPoint={editPenaltyPoint}
        onClose={handleAddPointDialogClose}
        onSave={handleAddPoint}
      />
    </>
  );
};

export default PenaltyDialog;
